import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import {
  H3,
  RadioGroup,
  Radio,
  Checkbox,
  Link,
  WarningNotice,
  CheckboxGroup,
  RadioControl,
  RadioLabel,
} from 'next/components/ui';
import { ConditionsShape } from 'next/entities/rules';

import { selectAccount } from 'next/entities/account';
import { SectionTitle, Section, HelpText, Targeting } from './styled';
import DomainFlag from './DomainFlag';

export function Environment({
  conditions = { or: [] },
  onChange,
  domains: accountDomains,
}) {
  const { or: domains } = conditions;
  const [radioValue, setRadioValue] = useState(
    domains.length > 0 ? 'specific' : 'all'
  );

  const disableUncheck = domains.length === 1;

  const hostNames = useMemo(
    () => domains.map(({ domains: { value } }) => value),
    [domains]
  );

  const handleHostOnChange = host => check => {
    if (check) {
      onChange({
        or: [...domains, { domains: { value: host, operator: '==' } }],
      });
    } else {
      onChange({
        or: domains.filter(
          ({ domains: currentDomain }) => currentDomain.value !== host
        ),
      });
    }
  };

  const allDomains = useMemo(
    () => [
      ...new Set([...accountDomains.split(',').filter(Boolean), ...hostNames]),
    ],
    [accountDomains, hostNames]
  );

  return (
    <Section>
      <H3>Environment targeting</H3>
      <SectionTitle>
        Which environment should this Pin Experience appear on?
      </SectionTitle>
      <RadioGroup
        value={radioValue}
        onValueChange={newValue => {
          setRadioValue(newValue);
          if (newValue === 'all') {
            onChange({ or: [] });
          } else if (newValue === 'specific' && allDomains.length > 0) {
            onChange({
              or: [{ domains: { value: allDomains[0], operator: '==' } }],
            });
          }
        }}
      >
        <RadioControl>
          <Radio id="all" value="all" />
          <RadioLabel htmlFor="all">
            Everywhere my Appcues embed script is installed
          </RadioLabel>
        </RadioControl>
        <RadioControl>
          <Radio id="specific" value="specific" />
          <RadioLabel htmlFor="specific">Only the domains I choose</RadioLabel>
        </RadioControl>

        {radioValue === 'specific' && (
          <Targeting>
            {disableUncheck && (
              <WarningNotice>
                Please leave at least one domain selected or select the option
                above.
              </WarningNotice>
            )}
            <CheckboxGroup>
              {allDomains.map(host => (
                <Checkbox
                  key={host}
                  onChange={handleHostOnChange(host)}
                  checked={hostNames.includes(host)}
                  disabled={hostNames.includes(host) && disableUncheck}
                >
                  <DomainFlag hostName={host} />
                </Checkbox>
              ))}
            </CheckboxGroup>
            <HelpText>
              Don’t see the domain you are looking for?{' '}
              <Link to="/settings/account">Update domain list</Link>
            </HelpText>
          </Targeting>
        )}
      </RadioGroup>
    </Section>
  );
}

Environment.propTypes = {
  conditions: ConditionsShape,
  onChange: PropTypes.func,
  domains: PropTypes.string,
};

const mapStateToProps = state => {
  const { domains = '' } = selectAccount(state);
  return {
    domains,
  };
};

export default connect(mapStateToProps)(Environment);
